// src/pages/social/Messages.jsx
import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaSearch, FaPlus, FaInbox } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';
import { MessageContext } from '../../context/messageContext';
import ConversationList from '../../components/social/ConversationList';
import Modal from '../../components/ui/Modal';
import UserList from '../../components/user/UserList';
import Loader from '../../components/common/Loader';
import userService from '../../services/userService';
import MessageForm from '../../components/social/MessageForm';

const Messages = () => {
  const { currentUser } = useContext(UserContext);
  const { fetchConversations, loading: conversationsLoading } = useContext(MessageContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Load conversations only once 
  const loadConversations = useCallback(async () => {
    if (currentUser && !initialLoadDone) {
      try {
        await fetchConversations();
      } finally {
        setInitialLoadDone(true);
      }
    }
  }, [currentUser, fetchConversations, initialLoadDone]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Load suggested users when modal opens - using useCallback to prevent infinite renders
  const loadSuggestedUsers = useCallback(async () => {
    try {
      const users = await userService.getSuggestedUsers();
      setSuggestedUsers(users);
    } catch (error) {
      console.error('Error loading suggested users:', error);
    }
  }, []);

  useEffect(() => {
    if (showNewMessageModal) {
      loadSuggestedUsers();
    }
  }, [showNewMessageModal, loadSuggestedUsers]);

  // Search users
  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await userService.searchUsers(searchTerm);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Select user to message
  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };

  // Handle message sent
  const handleMessageSent = () => {
    setShowNewMessageModal(false);
    setSelectedUser(null);
    fetchConversations();
  };

  if (!currentUser) {
    return null; // Or loading state while redirecting
  }

  return (
    <section className="py-6">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Messages</h1>
        
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="grid md:grid-cols-3 h-[calc(100vh-200px)]">
            {/* Sidebar with conversations */}
            <div className="md:col-span-1 border-r border-gray-200">
              <div className="p-4">
                {/* Search conversations */}
                <div className="relative mb-4">
                  <input 
                    type="search" 
                    placeholder="Search messages..." 
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
                
                {/* New message button */}
                <button 
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors mb-4"
                  onClick={() => setShowNewMessageModal(true)}
                >
                  <FaPlus /> New Message
                </button>
                
                {/* Conversations list */}
                <div className="h-[calc(100vh-300px)] overflow-y-auto">
                  {conversationsLoading && !initialLoadDone ? (
                    <div className="flex justify-center items-center h-40">
                      <Loader />
                    </div>
                  ) : (
                    <ConversationList />
                  )}
                </div>
              </div>
            </div>
            
            {/* Empty state or message thread */}
            <div className="md:col-span-2 flex items-center justify-center bg-gray-50">
              <div className="text-center max-w-md p-6">
                <div className="mb-4 flex justify-center">
                  <FaInbox className="text-gray-300" size={64} />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Select a conversation</h2>
                <p className="text-gray-600 mb-6">Choose an existing conversation or start a new one.</p>
                <button 
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
                  onClick={() => setShowNewMessageModal(true)}
                >
                  New Message
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* New Message Modal */}
        {showNewMessageModal && (
          <Modal 
            isOpen={showNewMessageModal} 
            onClose={() => {
              setShowNewMessageModal(false);
              setSelectedUser(null);
              setSearchTerm('');
              setSearchResults([]);
            }}
            title="New Message"
          >
            {!selectedUser ? (
              <div className="p-4">
                {/* Search users */}
                <div className="relative mb-6">
                  <input 
                    type="search" 
                    placeholder="Search users..." 
                    className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <button 
                    onClick={handleSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700"
                  >
                    <FaSearch />
                  </button>
                </div>
                
                {isSearching ? (
                  <div className="flex justify-center py-8">
                    <Loader />
                  </div>
                ) : searchResults.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Search Results</h3>
                    <UserList 
                      users={searchResults}
                      showFollowButton={false}
                      onUserSelect={handleSelectUser}
                    />
                  </div>
                ) : searchTerm && !isSearching ? (
                  <div className="text-center py-8 text-gray-500">
                    No users found matching "{searchTerm}"
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Suggested Users</h3>
                    <UserList 
                      users={suggestedUsers}
                      showFollowButton={false}
                      onUserSelect={handleSelectUser}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4">
                {/* Compose new message */}
                <div className="flex items-center border-b border-gray-200 pb-4 mb-4">
                  <div className="text-gray-600 mr-2">To:</div>
                  <div className="font-medium">{selectedUser.name}</div>
                  <button 
                    className="ml-auto text-sm text-primary hover:text-indigo-500"
                    onClick={() => setSelectedUser(null)}
                  >
                    Change
                  </button>
                </div>
                
                <MessageForm 
                  recipientId={selectedUser._id} 
                  onMessageSent={handleMessageSent}
                />
              </div>
            )}
          </Modal>
        )}
      </div>
    </section>
  );
};

export default Messages;